/* eslint-disable max-lines */
import moment from 'moment';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import ReactDatetime from 'react-datetime';
import PutOnLeaveSummary from './PutOnLeaveSummary';
import { useContext, useState, useEffect } from 'react';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import Loa from '../../../services/Loa';
import Employee from '../../../services/Employee';
import { toast } from 'react-toastify';

interface PutOnLeaveFormProps {
  isPutOnLeaveFormOpen: boolean;
  tooglePutOnLeaveForm: () => void;
  handleSubmitPutOnLeave: (v: any) => Promise<void>;
  putOnLeaveStep: number;
  loaUser: any;
  firstDayOfLoa: string | Date;
  setFirstDayOfLoa: (v: any) => void;
  lastDayOfLoa: string | Date;
  setLastDayOfLoa: (v: any) => void;
  typeOfLoa: string;
  setTypeOfLoa: (v: any) => void;
  selectedLoaPolicy: any;
  setSelectedLoaPolicy: (v: any) => void;
  selectedLoaReason: string;
  setSelectedLoaReason: (v: any) => void;
  reasonForLoa: string;
  setReasonForLoa: (v: any) => void;
  documents: File[];
  setDocuments: (v: any) => void;
  additionalNotes: string;
  setAdditionalNotes: (v: any) => void;
  setPutOnLeaveStep: (v: any) => void;
}

const PutOnLeaveForm = ({
  isPutOnLeaveFormOpen,
  tooglePutOnLeaveForm,
  handleSubmitPutOnLeave,
  putOnLeaveStep,
  loaUser,
  firstDayOfLoa,
  setFirstDayOfLoa,
  lastDayOfLoa,
  setLastDayOfLoa,
  typeOfLoa,
  setTypeOfLoa,
  selectedLoaPolicy,
  setSelectedLoaPolicy,
  selectedLoaReason,
  setSelectedLoaReason,
  reasonForLoa,
  setReasonForLoa,
  documents,
  setDocuments,
  additionalNotes,
  setAdditionalNotes,
  setPutOnLeaveStep,
}: PutOnLeaveFormProps) => {
  const { token, currentSchool }: any = useContext(AuthContext);
  const [loaPolicies, setLoaPolicies] = useState<any[]>([]);
  const [availableReasons, setAvailableReasons] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    if (isPutOnLeaveFormOpen && currentSchool?.value) {
      Loa(token)
        .loaPolicyList(currentSchool.value)
        .then((result) => {
          if (isMounted) setLoaPolicies(result.data || []);
        })
        .catch((e: any) => {
          if (isMounted) console.error('Error loading LOA policies:', e);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [isPutOnLeaveFormOpen, currentSchool?.value, token]);

  const handleLoaPolicyChange = (policy: any) => {
    setSelectedLoaPolicy(policy);
    setTypeOfLoa(policy.name);
    setAvailableReasons(policy.loa_reasons || []);
    setSelectedLoaReason('');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setDocuments([...documents, ...files]);
  };

  const removeDocument = (index: number) => {
    const newDocuments = documents.filter((_, i) => i !== index);
    setDocuments(newDocuments);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    if (e?.preventDefault) e?.preventDefault();

    if (isLoading) return;

    if (putOnLeaveStep === 0) {
      if (!firstDayOfLoa || !lastDayOfLoa || !selectedLoaPolicy) {
        toast.error('Please fill in all required fields');
        return;
      }
      setPutOnLeaveStep(1);
      return;
    }

    if (putOnLeaveStep === 1) {
      setIsLoading(true);

      try {
        const body = {
          employeeId: loaUser?.id,
          schoolId: currentSchool?.value,
          firstDayOfLoa,
          lastDayOfLoa,
          typeOfLoa,
          loaPolicyId: selectedLoaPolicy?.id || '',
          selectedReason: selectedLoaReason || '',
          reason: reasonForLoa,
          additionalNotes,
          documents,
        };

        await Employee(token).putOnLeaveWithDetails(body);

        toast.success('Employee successfully put on leave of absence');

        setFirstDayOfLoa('');
        setLastDayOfLoa('');
        setTypeOfLoa('');
        setSelectedLoaPolicy(null);
        setSelectedLoaReason('');
        setReasonForLoa('');
        setDocuments([]);
        setAdditionalNotes('');
        setAvailableReasons([]);
        setPutOnLeaveStep(0);

        tooglePutOnLeaveForm();

        await handleSubmitPutOnLeave(body);
      } catch (error: any) {
        console.error('Error putting employee on leave:', error);
        const message = String(error?.response?.data?.message || 'Failed to put employee on leave');
        toast.error(message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal className="modal-dialog-centered" isOpen={isPutOnLeaveFormOpen} toggle={tooglePutOnLeaveForm} size={'lg'}>
      <div className="modal-header">
        {putOnLeaveStep === 0 && (
          <h4 className="modal-title">
            Put {loaUser?.user?.firstName} {loaUser?.user?.lastName} on Leave of Absence
          </h4>
        )}
        {putOnLeaveStep === 1 && (
          <h4 className="modal-title">
            Review the information below and confirm to put {loaUser?.user?.firstName} on LOA
          </h4>
        )}
        <div onClick={tooglePutOnLeaveForm} className="d-flex p-1 cursor-pointer">
          <i className="fas fa-times" />
        </div>
      </div>
      <div className="modal-body">
        <div className="px-2">
          <Form onSubmit={handleFormSubmit}>
            {putOnLeaveStep === 0 && (
              <>
                <Row className="mb-4">
                  <Col lg="12">
                    <h5>Basic Information</h5>
                  </Col>
                </Row>

                <Row className="d-flex align-items-center mt-4">
                  <Col lg="4">
                    <Label className="form-control-label" htmlFor="firstDayOfLoa">
                      When is the First day of LOA?
                    </Label>
                  </Col>
                  <Col lg="8">
                    <FormGroup className="custom-datepicker mb-0">
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-calendar-grid-58" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <ReactDatetime
                          inputProps={{
                            placeholder: 'Select First Day of LOA',
                            id: 'firstDayOfLoa',
                            required: true,
                          }}
                          onChange={(newValue) => {
                            if (moment.isMoment(newValue)) {
                              setFirstDayOfLoa(newValue.format('MM/DD/YYYY'));
                            } else if (typeof newValue === 'string') {
                              setFirstDayOfLoa(newValue);
                            }
                          }}
                          timeFormat={false}
                          value={firstDayOfLoa ? moment(firstDayOfLoa, 'MM/DD/YYYY') : ''}
                          dateFormat={'MM/DD/YYYY'}
                          closeOnSelect={true}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="d-flex align-items-center mt-4">
                  <Col lg="4">
                    <Label className="form-control-label" htmlFor="lastDayOfLoa">
                      When is the Last day of LOA?
                    </Label>
                  </Col>
                  <Col lg="8">
                    <FormGroup className="custom-datepicker mb-0">
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-calendar-grid-58" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <ReactDatetime
                          inputProps={{
                            placeholder: 'Select Last Day of LOA',
                            id: 'lastDayOfLoa',
                            required: true,
                          }}
                          onChange={(newValue) => {
                            if (moment.isMoment(newValue)) {
                              setLastDayOfLoa(newValue.format('MM/DD/YYYY'));
                            } else if (typeof newValue === 'string') {
                              setLastDayOfLoa(newValue);
                            }
                          }}
                          timeFormat={false}
                          value={lastDayOfLoa ? moment(lastDayOfLoa, 'MM/DD/YYYY') : ''}
                          dateFormat={'MM/DD/YYYY'}
                          closeOnSelect={true}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="d-flex align-items-center mt-4">
                  <Col lg="4">
                    <Label className="form-control-label">Type of LOA</Label>
                  </Col>
                  <Col lg="8">
                    <UncontrolledDropdown>
                      <DropdownToggle caret color="default" className="form-control">
                        {selectedLoaPolicy?.name || 'Select LOA Policy'}
                      </DropdownToggle>
                      <DropdownMenu>
                        {loaPolicies.map((policy) => (
                          <DropdownItem key={policy.id} onClick={() => handleLoaPolicyChange(policy)}>
                            {policy.name}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </Col>
                </Row>

                {selectedLoaPolicy && availableReasons.length > 0 && (
                  <Row className="d-flex align-items-center mt-4">
                    <Col lg="4">
                      <Label className="form-control-label">Reason for LOA</Label>
                    </Col>
                    <Col lg="8">
                      <UncontrolledDropdown>
                        <DropdownToggle caret color="default" className="form-control">
                          {selectedLoaReason || 'Select Reason'}
                        </DropdownToggle>
                        <DropdownMenu>
                          {availableReasons.map((reason, index) => (
                            <DropdownItem key={index} onClick={() => setSelectedLoaReason(reason)}>
                              {reason}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </Col>
                  </Row>
                )}

                <Row className="d-flex align-items-start mt-4">
                  <Col lg="4">
                    <Label className="form-control-label">What is the reason for LOA / Notes</Label>
                  </Col>
                  <Col lg="8">
                    <FormGroup>
                      <Input
                        type="textarea"
                        rows="4"
                        value={reasonForLoa}
                        onChange={(e) => setReasonForLoa(e.target.value)}
                        placeholder="Enter reason for leave of absence..."
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <hr />

                <Row className="mb-4">
                  <Col lg="12">
                    <h5>Documents</h5>
                    <p className="text-muted">A place to upload documents (not required)</p>
                  </Col>
                </Row>

                <Row className="d-flex align-items-center mt-4">
                  <Col lg="12">
                    <FormGroup>
                      <Label for="documentUpload">Upload Documents</Label>
                      <Input
                        id="documentUpload"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />
                      <small className="form-text text-muted">Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG</small>
                    </FormGroup>
                  </Col>
                </Row>

                {documents.length > 0 && (
                  <Row className="mt-3">
                    <Col lg="12">
                      <Label>Uploaded Documents:</Label>
                      {documents.map((doc, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded"
                        >
                          <span>{doc.name}</span>
                          <Button color="danger" size="sm" onClick={() => removeDocument(index)}>
                            Remove
                          </Button>
                        </div>
                      ))}
                    </Col>
                  </Row>
                )}

                <hr />

                <Row className="mb-4">
                  <Col lg="12">
                    <h5>Additional Notes</h5>
                  </Col>
                </Row>

                <Row className="d-flex align-items-start mt-4">
                  <Col lg="12">
                    <FormGroup>
                      <Label>Notes</Label>
                      <Input
                        type="textarea"
                        rows="4"
                        value={additionalNotes}
                        onChange={(e) => setAdditionalNotes(e.target.value)}
                        placeholder="Enter any additional notes..."
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end mt-4">
                  <div>
                    <Button className="btn-secondary" onClick={tooglePutOnLeaveForm} disabled={isLoading}>
                      Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={isLoading}>
                      {isLoading ? 'Loading...' : 'Next'}
                    </Button>
                  </div>
                </div>
              </>
            )}

            {putOnLeaveStep === 1 && (
              <>
                <PutOnLeaveSummary
                  loaUser={loaUser}
                  firstDayOfLoa={firstDayOfLoa}
                  lastDayOfLoa={lastDayOfLoa}
                  typeOfLoa={typeOfLoa}
                  selectedLoaPolicy={selectedLoaPolicy}
                  selectedLoaReason={selectedLoaReason}
                  reasonForLoa={reasonForLoa}
                  documents={documents}
                  additionalNotes={additionalNotes}
                />

                <div className="d-flex justify-content-end mt-4">
                  <div>
                    <Button className="btn-secondary" onClick={() => setPutOnLeaveStep(0)} disabled={isLoading}>
                      Back
                    </Button>
                    <Button color="warning" type="submit" disabled={isLoading}>
                      {isLoading ? 'Processing...' : 'Confirm Put On Leave'}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default PutOnLeaveForm;
